import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Badge - Status indicators, tags, labels
 * 
 * API zgodne z Radix Themes
 * - Rozmiary: "1" | "2" | "3"
 * - Warianty: "soft" | "solid" | "surface" | "outline"
 * - Colors: "pink" | "gray" | "success" | "danger" | "warning"
 * 
 * @example
 * ```tsx
 * <Badge variant="solid" color="pink">Nowy</Badge>
 * <Badge variant="soft" color="success">Aktywny</Badge>
 * <Badge variant="outline" size="1">Beta</Badge>
 * ```
 */

const badgeVariants = cva(
  cn(
    "inline-flex items-center justify-center",
    "font-[var(--font-weight-medium)]",
    "whitespace-nowrap",
    "transition-colors duration-150"
  ),
  {
    variants: {
      variant: {
        // Soft - subtelne kolorowe tło
        soft: "",
        // Solid - wyraźne kolorowe tło
        solid: "",
        // Surface - neutralne tło z borderem
        surface: cn(
          "bg-[var(--dark-a2)] text-[var(--text-white-85)]",
          "border border-[var(--dark-a6)]"
        ),
        // Outline - transparentne z borderem
        outline: "bg-transparent border",
      },
      color: {
        pink: "",
        gray: "",
        success: "",
        danger: "",
        warning: "",
      },
      size: {
        "1": cn(
          "h-[18px] px-[var(--space-2)]",
          "text-[var(--font-size-1)]",
          "rounded-[var(--radius-sm)]",
          "gap-[var(--space-1)]"
        ),
        "2": cn(
          "h-[22px] px-[var(--space-3)]",
          "text-[var(--font-size-2)]",
          "rounded-[var(--radius-base)]",
          "gap-[var(--space-2)]"
        ),
        "3": cn(
          "h-[26px] px-[var(--space-4)]",
          "text-[var(--font-size-3)]",
          "rounded-[var(--radius-md)]",
          "gap-[var(--space-2)]"
        ),
      },
    },
    compoundVariants: [
      // Pink variants
      {
        variant: "soft",
        color: "pink",
        className: "bg-[var(--pink-a3)] text-[var(--pink-11)]",
      },
      {
        variant: "solid",
        color: "pink",
        className: "bg-[var(--pink-9)] text-white",
      },
      {
        variant: "outline",
        color: "pink",
        className: "border-[var(--pink-a7)] text-[var(--pink-11)]",
      },
      // Gray variants
      {
        variant: "soft",
        color: "gray",
        className: "bg-[var(--gray-a3)] text-[var(--gray-11)]",
      },
      {
        variant: "solid",
        color: "gray",
        className: "bg-[var(--gray-9)] text-white",
      },
      {
        variant: "outline",
        color: "gray",
        className: "border-[var(--gray-a7)] text-[var(--gray-11)]",
      },
      // Success variants
      {
        variant: "soft",
        color: "success",
        className: "bg-[var(--success-a3)] text-[var(--success-11)]",
      },
      {
        variant: "solid",
        color: "success",
        className: "bg-[var(--success-9)] text-white",
      },
      {
        variant: "outline",
        color: "success",
        className: "border-[var(--success-a7)] text-[var(--success-11)]",
      },
      // Danger variants
      {
        variant: "soft",
        color: "danger",
        className: "bg-[var(--danger-a3)] text-[var(--danger-11)]",
      },
      {
        variant: "solid",
        color: "danger",
        className: "bg-[var(--danger-9)] text-white",
      },
      {
        variant: "outline",
        color: "danger",
        className: "border-[var(--danger-a7)] text-[var(--danger-11)]",
      },
      // Warning variants
      {
        variant: "soft",
        color: "warning",
        className: "bg-[var(--warning-a3)] text-[var(--warning-11)]",
      },
      {
        variant: "solid",
        color: "warning",
        className: "bg-[var(--warning-9)] text-white",
      },
      {
        variant: "outline",
        color: "warning",
        className: "border-[var(--warning-a7)] text-[var(--warning-11)]",
      },
    ],
    defaultVariants: {
      variant: "soft",
      color: "gray",
      size: "2",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Icon to display before text
   */
  icon?: React.ReactNode
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, color, size, icon, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, color, size }), className)}
        {...props}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        {children}
      </span>
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }

